import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth, homePathFor } from "@/lib/auth";
import { ClockClockwise, CheckSquareOffset, GearSix, SignOut, ChartBar, ChartPieSlice, ArrowLeft } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { ThemeToggle } from "@/components/ThemeToggle";
import { ModuleSwitcher } from "@/components/ModuleSwitcher";
import { TrakLogo } from "@/components/TrakLogo";
import { Breadcrumbs, BreadcrumbProvider } from "@/components/Breadcrumbs";

export default function TimesheetLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin" || user?.role === "timesheet_admin" || user?.timesheet_role === "admin";
  const isManager = isAdmin || user?.timesheet_role === "manager" || user?.role === "soc_lead";
  const goHome = () => nav(homePathFor(user));

  const links = [
    { to: "/timesheet", label: "My Timesheet", icon: ClockClockwise, exact: true, testid: "ts-nav-my", show: true },
    { to: "/timesheet/dashboard", label: "Dashboard", icon: ChartPieSlice, testid: "ts-nav-dashboard", show: true },
    { to: "/timesheet/approvals", label: "Approvals", icon: CheckSquareOffset, testid: "ts-nav-approvals", show: isManager },
    { to: "/timesheet/reports", label: "Timesheet-Reports", icon: ChartBar, testid: "ts-nav-reports", show: isManager },
    { to: "/timesheet/admin", label: "Configuration", icon: GearSix, testid: "ts-nav-admin", show: isAdmin },
  ].filter((l) => l.show);

  const linkClass = ({ isActive }) => cn(
    "flex items-center gap-3 px-5 py-2.5 text-sm transition-colors border-l-2 border-transparent",
    "text-sidebar-muted hover:text-white hover:bg-white/5",
    "dark:text-muted-foreground dark:hover:text-foreground dark:hover:bg-accent/40",
    isActive && "text-white bg-white/10 border-white dark:text-foreground dark:bg-primary-tint dark:border-primary"
  );

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <aside className="w-60 shrink-0 border-r flex flex-col bg-sidebar text-sidebar-foreground border-sidebar-border dark:bg-background dark:text-foreground dark:border-border">
        <div className="h-14 px-5 border-b border-sidebar-border dark:border-border flex items-center gap-2">
          <button
            data-testid="ts-home-logo"
            onClick={goHome}
            title="Back to start"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer"
          >
            <div className="h-7 w-7 grid place-items-center bg-white text-sidebar dark:bg-primary dark:text-primary-foreground rounded-sm">
              <TrakLogo size={18} />
            </div>
            <div className="font-display font-bold tracking-tight">TRAK</div>
          </button>
          <span className="ml-auto text-[10px] font-mono text-sidebar-muted dark:text-muted-foreground uppercase">Time</span>
        </div>
        <nav className="flex-1 py-3 overflow-y-auto">
          <button
            data-testid="ts-nav-back"
            onClick={() => nav(-1)}
            className="w-full flex items-center gap-3 px-5 py-2.5 text-sm text-sidebar-muted hover:text-white hover:bg-white/5 dark:text-muted-foreground dark:hover:text-foreground dark:hover:bg-accent/40 transition-colors border-l-2 border-transparent"
          >
            <ArrowLeft size={16} weight="regular" /> Back
          </button>
          {links.map(({ to, label, icon: Icon, exact, testid }) => (
            <NavLink key={to} to={to} end={exact} data-testid={testid} className={linkClass}>
              <Icon size={16} weight="regular" /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t p-4 border-sidebar-border dark:border-border">
          <div className="text-xs font-mono text-sidebar-muted dark:text-muted-foreground">SIGNED IN AS</div>
          <div className="text-sm mt-1">{user?.name}</div>
          <div className="text-[11px] font-mono text-sidebar-muted dark:text-muted-foreground">{user?.email}</div>
          <div className="mt-2 flex items-center justify-between">
            <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border border-sidebar-border dark:border-border">
              Timesheet {isAdmin ? "admin" : isManager ? "manager" : "user"}
            </span>
            <button
              data-testid="ts-logout-button"
              onClick={async () => { await logout(); nav("/login", { replace: true }); }}
              className="text-sidebar-muted hover:text-white dark:text-muted-foreground dark:hover:text-primary"
              title="Sign out"
            ><SignOut size={16} /></button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <BreadcrumbProvider>
          <header className="h-14 border-b flex items-center px-6 gap-4 bg-sidebar text-sidebar-foreground border-sidebar-border dark:bg-background dark:text-foreground dark:border-border">
            <Breadcrumbs />
            <div className="ml-auto flex items-center gap-4">
              <ModuleSwitcher />
              <ThemeToggle />
            </div>
          </header>
          <main className="flex-1 overflow-y-auto">
            <Outlet />
          </main>
        </BreadcrumbProvider>
      </div>
    </div>
  );
}
